import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParticipants } from "../../contexts/ParticipantContext";

const STAGES = [
  "Received",
  "Bisque Fired",
  "Glazed",
  "Glaze Fired",
  "Ready for Pickup",
  "Collected",
  "Damaged",
];

const BADGE = {
  Received:
    "bg-sky-100 text-sky-800 dark:bg-sky-400/20 dark:text-sky-200 dark:border-sky-300/30",
  "Bisque Fired":
    "bg-amber-100 text-amber-800 dark:bg-amber-400/20 dark:text-amber-200 dark:border-amber-300/30",
  Glazed:
    "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-400/20 dark:text-fuchsia-200 dark:border-fuchsia-300/30",
  "Glaze Fired":
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-400/20 dark:text-emerald-200 dark:border-emerald-300/30",
  "Ready for Pickup":
    "bg-green-100 text-green-800 dark:bg-green-400/20 dark:text-green-200 dark:border-green-300/30",
  Collected:
    "bg-zinc-200 text-zinc-800 dark:bg-zinc-400/20 dark:text-zinc-200 dark:border-zinc-300/30",
  Damaged:
    "bg-red-100 text-red-800 dark:bg-red-400/20 dark:text-red-200 dark:border-red-300/30",
};

const LS_PIECES = "mudoPieces";

const j = {
  get: (k, f = null) => {
    try {
      const v = localStorage.getItem(k);
      return v ? JSON.parse(v) : f;
    } catch {
      return f;
    }
  },
  set: (k, v) => {
    try {
      localStorage.setItem(k, JSON.stringify(v));
    } catch {}
  },
};

export default function ParticipantsPage({ isDark = true, onBack }) {
  const navigate = useNavigate();
  const {
    pieces = [],
    participants = [],
    updatePiece,
    setPieceStage,
  } = useParticipants() || {};

  const writeStage = (id, stage) => {
    if (typeof updatePiece === "function") {
      updatePiece(id, { stage, updatedAt: Date.now() });
      return;
    }
    if (typeof setPieceStage === "function") {
      setPieceStage(id, stage);
      return;
    }
    const cur = Array.isArray(pieces) ? [...pieces] : j.get(LS_PIECES, []);
    const next = cur.map((p) =>
      p.id === id ? { ...p, stage, updatedAt: Date.now() } : p
    );
    j.set(LS_PIECES, next);
    window.dispatchEvent(new Event("mudo:participants-updated"));
  };

  // filters
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("All");
  const [workshopFilter, setWorkshopFilter] = useState("All");
  const [onlyUncollected, setOnlyUncollected] = useState(false);

  // selection (page-scope)
  const [selectedIds, setSelectedIds] = useState({});
  const toggleSelect = (id) => setSelectedIds((m) => ({ ...m, [id]: !m[id] }));
  const clearSelection = () => setSelectedIds({});

  const workshops = useMemo(() => {
    const s = new Set();
    (pieces || []).forEach((p) => p.workshopTitle && s.add(p.workshopTitle));
    return ["All", ...Array.from(s)];
  }, [pieces]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (pieces || [])
      .filter((p) => {
        if (stageFilter !== "All" && (p.stage || "Received") !== stageFilter) return false;
        if (workshopFilter !== "All" && (p.workshopTitle || "") !== workshopFilter) return false;
        if (onlyUncollected && (p.stage === "Collected" || p.stage === "Damaged")) return false;
        if (!needle) return true;
        const hay = [
          p.title,
          p.defaultTitle,
          p.name,
          p.participantName,
          p.email,
          p.workshopTitle,
          p.workshopId,
          p.bookingId,
          p.stage,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      })
      .sort((a, b) => {
        const A = a.updatedAt || a.createdAt || 0;
        const B = b.updatedAt || b.createdAt || 0;
        return B - A;
      });
  }, [pieces, q, stageFilter, workshopFilter, onlyUncollected]);

  const stats = useMemo(() => {
    const byStage = new Map();
    (pieces || []).forEach((p) => {
      const s = p.stage || "Received";
      byStage.set(s, (byStage.get(s) || 0) + 1);
    });
    return {
      totalParticipants: participants.length || 0,
      totalPieces: pieces.length || 0,
      ready: byStage.get("Ready for Pickup") || 0,
      collected: byStage.get("Collected") || 0,
      byStage,
    };
  }, [pieces, participants]);

  const selectedArray = useMemo(
    () => Object.entries(selectedIds).filter(([, v]) => v).map(([k]) => k),
    [selectedIds]
  );

  const bulkSetStage = (stage) => {
    if (!selectedArray.length) return;
    selectedArray.forEach((id) => writeStage(id, stage));
    clearSelection();
  };

  const exportCSV = () => {
    const rows = [];
    rows.push([
      "PieceID",
      "Title",
      "Participant",
      "Email",
      "WorkshopTitle",
      "WorkshopId",
      "BookingId",
      "Stage",
      "CreatedAt",
      "UpdatedAt",
    ]);
    filtered.forEach((p) => {
      rows.push([
        p.id || "",
        p.title || p.defaultTitle || "",
        p.name || p.participantName || "",
        p.email || "",
        p.workshopTitle || "",
        p.workshopId || "",
        p.bookingId || "",
        p.stage || "Received",
        p.createdAt ? new Date(p.createdAt).toISOString() : "",
        p.updatedAt ? new Date(p.updatedAt).toISOString() : "",
      ]);
    });
    const csv = rows
      .map((r) =>
        r
          .map((cell) => {
            const s = String(cell ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `participants_pieces_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // helpers for page select
  const pageIds = filtered.map((p) => String(p.id));
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => !!selectedIds[id]);

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      if (allOnPageSelected) {
        const next = { ...prev };
        pageIds.forEach((id) => delete next[id]);
        return next;
      }
      const next = { ...prev };
      pageIds.forEach((id) => (next[id] = true));
      return next;
    });
  };

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? "text-white" : "text-neutral-900"}`}
      style={{ backgroundColor: 'var(--color-app-bg)' }}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <img src="/src/assets/mudo-logo-bg.png" alt="Mudo" className="w-6 h-6 opacity-90" />
          <h1 className="text-2xl font-bold">Participants & Pieces</h1>
        </div>
        <button
          onClick={() => (onBack ? onBack() : navigate("/dashboard"))}
          className="px-3 py-1 rounded text-white transition-colors"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded border border-white/10 p-3">
          <div className="text-[11px] opacity-70">Participants</div>
          <div className="text-xl font-semibold">{stats.totalParticipants}</div>
        </div>
        <div className="rounded border border-white/10 p-3">
          <div className="text-[11px] opacity-70">Pieces</div>
          <div className="text-xl font-semibold">{stats.totalPieces}</div>
        </div>
        <div className="rounded border border-white/10 p-3">
          <div className="text-[11px] opacity-70">Ready for Pickup</div>
          <div className="text-xl font-semibold">{stats.ready}</div>
        </div>
        <div className="rounded border border-white/10 p-3">
          <div className="text-[11px] opacity-70">Collected</div>
          <div className="text-xl font-semibold">{stats.collected}</div>
        </div>
      </div>

      {/* Filters & actions */}
      <div className="rounded border border-white/10 p-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, workshop, stage…"
            className="px-3 py-2 rounded bg-neutral-800 border border-neutral-700"
            aria-label="Search"
          />

          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="px-3 py-2 rounded bg-neutral-800 border border-neutral-700"
            aria-label="Filter by stage"
          >
            <option>All</option>
            {STAGES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>

          <select
            value={workshopFilter}
            onChange={(e) => setWorkshopFilter(e.target.value)}
            className="px-3 py-2 rounded bg-neutral-800 border border-neutral-700"
            aria-label="Filter by workshop"
          >
            {workshops.map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={onlyUncollected}
              onChange={(e) => setOnlyUncollected(e.target.checked)}
            />
            <span className="text-sm">Hide collected & damaged</span>
          </label>

          <button
            type="button"
            onClick={() => {
              setQ("");
              setStageFilter("All");
              setWorkshopFilter("All");
              setOnlyUncollected(false);
            }}
            className="px-3 py-2 rounded bg-neutral-700 hover:bg-neutral-600 text-sm"
            title="Clear all filters"
          >
            Clear Filters
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button
            onClick={() => bulkSetStage("Ready for Pickup")}
            disabled={!selectedArray.length}
            className={`px-3 py-1 rounded transition-colors ${
              selectedArray.length ? "bg-green-600 hover:bg-green-700" : "bg-green-900/40 cursor-not-allowed"
            }`}
          >
            Mark Ready
          </button>
          <button
            onClick={() => bulkSetStage("Collected")}
            disabled={!selectedArray.length}
            className={`px-3 py-1 rounded transition-colors ${
              selectedArray.length ? "bg-sky-600 hover:bg-sky-700" : "bg-sky-900/40 cursor-not-allowed"
            }`}
          >
            Mark Collected
          </button>
          <button
            onClick={clearSelection}
            disabled={!selectedArray.length}
            className={`px-3 py-1 rounded transition-colors ${
              selectedArray.length ? "bg-neutral-700 hover:bg-neutral-600" : "bg-neutral-800/60 cursor-not-allowed"
            }`}
          >
            Clear Selection
          </button>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={exportCSV}
              className="px-3 py-1 rounded bg-emerald-500 text-black hover:bg-emerald-400 font-semibold transition-colors"
              title="Export filtered list to CSV"
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded border border-white/10">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left opacity-80">
              <th className="py-2 px-3">
                <button
                  type="button"
                  onClick={toggleSelectAllOnPage}
                  className="px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                  title={allOnPageSelected ? "Unselect all on page" : "Select all on page"}
                >
                  {allOnPageSelected ? "Unselect All" : "Select All"}
                </button>
              </th>
              <th className="py-2 px-3">Piece</th>
              <th className="py-2 px-3">Participant</th>
              <th className="py-2 px-3">Workshop</th>
              <th className="py-2 px-3">Stage</th>
              <th className="py-2 px-3 text-right">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {filtered.map((p) => {
              const updated = new Date(p.updatedAt || p.createdAt || Date.now()).toLocaleString();
              return (
                <tr key={p.id}>
                  <td className="py-2 px-3">
                    <input
                      type="checkbox"
                      checked={!!selectedIds[p.id]}
                      onChange={() => toggleSelect(p.id)}
                      aria-label={`Select ${p.title || p.defaultTitle || p.id}`}
                    />
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-medium">{p.title || p.defaultTitle || "Untitled"}</div>
                    <div className="text-xs opacity-70">#{p.id}</div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-medium">{p.name || p.participantName || "—"}</div>
                    <div className="text-xs opacity-70">{p.email || "—"}</div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="min-w-0 max-w-[28ch] truncate">{p.workshopTitle || p.workshopId || "—"}</div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[10px] px-2 py-1 rounded-full border ${
                          BADGE[p.stage] ||
                          "bg-zinc-100 text-zinc-800 dark:bg-zinc-400/20 dark:text-zinc-200 dark:border-zinc-300/30"
                        }`}
                      >
                        {p.stage || "Received"}
                      </span>
                      <select
                        value={p.stage || "Received"}
                        onChange={(e) => writeStage(p.id, e.target.value)}
                        className="px-2 py-1 rounded bg-neutral-800 border border-neutral-700"
                        title="Update stage"
                      >
                        {STAGES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right">{updated}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center opacity-70">
                  No matching pieces.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
